import { useActor } from '@xstate/react';
import * as React from 'react';
import { FC, useEffect, useMemo } from 'react';

import { Address, Recipient } from 'api/api';

import { Spinner } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { AnimatedButton, Button } from '~src/ui/button';
import { DialogLayout } from '~src/ui/dialog-layout';

import { DeleteAddressOverlay } from '~address-form/address-list/delete-address-overlay';
import {
  AddressFormBusinessProvider,
  useAddressFormBusinessLogic,
} from '~address-form/context/use-address-form';
import { DeliveryAddressFormWithSuggestion } from '~address-form/form-templates/delivery-address-form-with-suggestion';
import { isDeliveryAddressEmpty } from '~address-form/utils/prepare-address';

import { useFSMContext } from '../context/use-fsm-context';
import { ExtendedValidationHandler } from '../state-machines/shared-types';
import { COUNTRIES_WITH_EXTENDED_REGION_VALIDATION } from '../state-machines/suggestion-service';
import { getModalTitle } from '../utils/modal-utils';

interface BillingFormFullscreenContentProps {
  triggerExtendedValidation: ExtendedValidationHandler;
  handleDelete: (address: Recipient) => void;
  hideDeleteButton?: boolean;
}

export const BillingFormFullscreenContent: FC<BillingFormFullscreenContentProps> = ({
  triggerExtendedValidation,
  handleDelete,
  hideDeleteButton,
}) => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();
  const [overlayOpen, setOverlayOpen] = React.useState(false);

  const { cleanForm, isFormClear, addressService } = useAddressFormBusinessLogic();
  const [parentState] = useActor(addressService);
  const [state] = useActor(parentState.context.suggestion);

  const { billingState: machineState } = useFSMContext();

  useEffect(() => {
    if (machineState?.context.cleanForm) {
      cleanForm();
      setOverlayOpen(false);
    }
  }, [machineState]);

  const suggestionUnavailable =
    state.matches('unverified') ||
    state.matches('ready') ||
    !state.context.validation?.suggestion?.address;

  const addressFormButtons = overlayOpen ? (
    // we're rendering empty div here to prevent render of the default form buttons
    <div css={{ height: 128 }} />
  ) : (
    <>
      <AnimatedButton type="submit" size="normal" animKey={`${machineState?.meta.fetching}`}>
        {machineState?.meta.fetching ? (
          <Spinner size="small" />
        ) : suggestionUnavailable ? (
          tICU('widget.ADDRESS_MODAL.SAVE_BTN')
        ) : (
          tICU('widget.delivery_address.SAVE_ANYWAY')
        )}
      </AnimatedButton>
      <Button
        css={theme => ({ color: theme.error35, marginTop: '8px' })}
        buttonType="tertiary"
        onClick={e => {
          e.preventDefault();
          setOverlayOpen(true);
        }}
      >
        {tICU('widget.delivery_address.DELETE_ADDRESS_BTN')}
      </Button>
    </>
  );

  const getFulscreenButtonComponent = () => {
    if (hideDeleteButton) {
      return undefined;
    }

    if (
      !isDeliveryAddressEmpty(
        machineState?.context.currentAddress || {},
        globalState.address_form_configuration?.features?.frozen_fields_after_prefill?.fields
      ) &&
      globalState.address_book?.addresses &&
      !isFormClear()
    ) {
      return addressFormButtons;
    }

    return undefined;
  };

  return (
    <>
      <DeliveryAddressFormWithSuggestion
        triggerExtendedValidation={triggerExtendedValidation}
        formButtons={getFulscreenButtonComponent()}
        fetching={machineState?.meta.fetching}
      />
      {!!machineState?.context.currentAddress && overlayOpen && (
        <DeleteAddressOverlay
          onCancel={() => setOverlayOpen(false)}
          onDelete={() => handleDelete(machineState.context.currentAddress!)}
          isFetching={machineState.meta.fetching}
        />
      )}
    </>
  );
};

interface BillingFormFullscreenFSMProps {
  triggerExtendedValidation: ExtendedValidationHandler;
  hideDeleteButton?: boolean;
  handleClose: () => void;
  handleSubmit: (address: Recipient) => void;
  handleDelete: (address: Recipient) => void;
  extendedAddress?: Address;
  setExtendedAddress: React.Dispatch<React.SetStateAction<Address | undefined>>;
}

export const BillingFormFullscreenFSM: FC<BillingFormFullscreenFSMProps> = ({
  triggerExtendedValidation,
  hideDeleteButton = false,
  handleClose,
  handleSubmit,
  handleDelete,
  extendedAddress,
  setExtendedAddress,
}) => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();

  const { billingState: machineState } = useFSMContext();

  const getAddressVerified = () => {
    if (
      globalState.address_form_configuration?.global_features?.enable_extended_validation &&
      COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(globalState.purchase_country)
    )
      return false;

    return !machineState?.context.currentAddress?.is_dirty;
  };

  const modalTitle = useMemo(
    () =>
      getModalTitle(
        // TODO fix this
        machineState!.context,
        globalState.address_form_configuration?.features?.frozen_fields_after_prefill?.fields
      ),

    [
      machineState?.context,
      globalState.address_form_configuration?.features?.frozen_fields_after_prefill?.fields,
    ]
  );

  return (
    <DialogLayout title={tICU(modalTitle)} onClose={handleClose}>
      <AddressFormBusinessProvider
        onSubmit={address => handleSubmit(address)}
        address={machineState?.context.currentAddress}
        isBillingAddress={true}
        isModalForm
        extendedAddress={extendedAddress}
        setExtendedAddress={setExtendedAddress}
        /**
         * The possible scenarios for address modal are:
         * - editing the address that already exists in the address book
         * - adding new address to address book
         * - confirming the address (external address book scenario)
         * - editing address that doesn't pass external validation
         */
        addressVerified={getAddressVerified()}
      >
        <BillingFormFullscreenContent
          triggerExtendedValidation={triggerExtendedValidation}
          handleDelete={handleDelete}
          hideDeleteButton={hideDeleteButton}
        />
      </AddressFormBusinessProvider>
    </DialogLayout>
  );
};
